import React from "react";
import useActive from "../../../../hooks/useActive";
import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import RenderIf from "../../../Common/RenderIf/RenderIf";
import { useCreateElementContext } from "../Context/CreateElementContext";
import { isEmpty } from "lodash-es";
import { ErrorLabel } from "../../ElementForm";
import AssessmentDetails from "./AssessmentDetails";
import AssessmentContent from "./AssessmentContent";

const steps = ["Add details", "Add content"];

const REQUIRED_MESSAGE = "This field is required";

const validations = {
  0: ({ values }) => {
    const errors = {};
    if (!values?.pass_percentage || values?.pass_percentage === "0")
      errors.pass_percentage = REQUIRED_MESSAGE;
    if (
      !!values?.randomize_questions?.randomize_questions &&
      parseInt(values?.randomize_questions_count) === 0
    )
      errors.randomize_questions_count = REQUIRED_MESSAGE;
    else if (
      values?.randomize_questions?.randomize_questions &&
      parseInt(values?.randomize_questions_count) < 1
    ) {
      errors.randomize_questions_count = "Please add a valid value";
    }
    if (!values?.thumbnail?.portrait)
      errors["thumbnail.portrait"] = REQUIRED_MESSAGE;
    if (!values?.thumbnail?.landscape)
      errors["thumbnail.landscape"] = REQUIRED_MESSAGE;

    return errors;
  },
  1: ({ values }) => {
    const errors = {};
    const noOfLanguages = values?.content?.length;
    if (noOfLanguages === 0)
      errors.content = "Please add at least one language set";
    else {
      if (
        new Set(values.content.map((language) => language?.questions?.length))
          .size !== 1
      ) {
        errors.content = "Please add the same no of questions in all languages";
      } else {
        // if the no of questions are same, then will check for options
        const noOfQuestions = values.content[0]?.questions?.length;
        const questions = values.content[0]?.questions;
        const content = values.content;
        if (noOfQuestions === 0) {
          errors.content = "Please add at least one question";
        } else if (questions.some(({ choices }) => choices?.length < 2)) {
          errors.content = "Please add at least two option";
        } else if (
          values.assessment_type === "QUIZ" &&
          questions.some(({ answers }) => !answers?.length)
        ) {
          errors.content = "Please add at least one answer";
        } else {
          questions.forEach((_, index) => {
            const answers = new Set();
            const options = new Set();
            content.forEach((lang) => {
              answers.add(lang.questions[index]?.answers?.length ?? 0);
              options.add(lang.questions[index].choices.length);
            });
            if (values.assessment_type === "QUIZ" && answers.size !== 1) {
              errors.content = "Please mark the same answers in all languages";
            }
            if (options.size !== 1) {
              errors.content = "Please add the same options for all languages";
            }
          });

          // checking contents of the questions for validation
          content.forEach((lang, contentIndex) => {
            if (!lang.title)
              errors[`content.${contentIndex}.title`] = "Please add a title";
            if (!lang.description)
              errors[`content.${contentIndex}.description`] =
                "Please add a description";

            const hasLink = !!lang?.questions[0]?.["link"];
            lang.questions.forEach((question, questionIndex) => {
              const key = `content.${contentIndex}.questions.${questionIndex}`;
              if (!!lang.questions[questionIndex]?.["link"] !== hasLink) {
                errors.content = "Either add links in all question or none.";
              }
              if (question.question.length === 0) {
                errors[`${key}.question`] = "This field is required";
              }
              if (question.choices.filter((item) => item).length === 0) {
                errors[`${key}.choices`] = "This field is required";
              }
            });
          });
        }
      }
    }
    return errors;
  },
};

const AssessmentForm = ({}) => {
  const { active, handleActive } = useActive(0);
  const { form, handleCreate } = useCreateElementContext();

  const handleNext = () => {
    const validation = validations[active];

    const errors = validation({ values: form.values });

    if (isEmpty(errors)) {
      if (active === 0) {
      }
      if (active === 1) {
        handleCreate();
      } else {
        handleActive((active) => active + 1);
      }
    }
    form.setErrors(errors);
  };
  const handleBack = () => {
    if (active > 0) handleActive((active) => active - 1);
  };

  const errorMessage =
    Object.values(form.errors).filter(Boolean).length > 1
      ? `${
          Object.values(form.errors).filter(Boolean).length
        } errors, please correct`
      : Object.values(form.errors).join(", ");

  return (
    <Box>
      <Stepper
        activeStep={active}
        sx={{
          maxWidth: "656px",
          width: "75%",
          my: 3,
          mx: "auto",
        }}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box sx={{ border: "1px solid #D6D6D6", p: 4, borderRadius: 2 }}>
        <RenderIf condition={active === 0}>
          <AssessmentDetails />
        </RenderIf>
        <RenderIf condition={active === 1}>
          <AssessmentContent />
        </RenderIf>
      </Box>
      <Box
        mt={4}
        gap={2}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <ErrorLabel label={errorMessage} />
        <Box ml="auto" gap={2} display={"flex"}>
          <Button
            onClick={handleBack}
            //   disabled={isPending}
            color="error"
            variant="outlined"
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            //   disabled={isPending}
            color="error"
            variant="contained"
          >
            {active === 1 ? "Submit" : "Next"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AssessmentForm;
